/**
 * React Query hook + helpers for Reports > Store Analytics.
 *
 *   useStoreAnalytics(filters, enabled)   GET /v1/retail/store-analytics
 *   downloadStoreAnalyticsXlsx(filters)   GET /v1/retail/store-analytics/export
 *
 * Returns, discounts and bulk penetration by store, in three sections of one report.
 * The api answers for the section on screen only (the other two are null) and returns
 * it already narrowed by the filters, so the page renders it as given (sorting is
 * internal to the shared screen). The workbook holds every section.
 *
 * Contract:
 *
 *   GET /v1/retail/store-analytics
 *     ?section=returns|discounts|bulk   optional, default returns
 *     &grain=week|day                   optional, default week (Returns and Discounts columns)
 *     &from=YYYY-MM-DD&to=YYYY-MM-DD    optional window of Returns and Discounts
 *     &through=YYYY-MM-DD               optional as-of day of Bulk penetration
 *     &compare=none                     optional, default last_year
 *     &store_no=...                     optional, repeatable (a filter, never a scope)
 *     &disc=...                         optional, repeatable discount ids (Discounts)
 *   -> { section, as_of, data_through, data_from, scope, compare,
 *        returns, discounts, bulk }
 *
 * The types come from `@ampersandbrands/ui`, because the shared screen owns the wire
 * shape. The daily email goes through the generic `useReportSubscriptions` hook; what
 * lives here is the pure pair that decides which filters a subscription saves. Same
 * 404/503 "not deployed yet" convention as the other report hooks.
 */

import { useQuery } from '@tanstack/react-query';
import type {
  StoreAnalyticsCompare,
  StoreAnalyticsFilters,
  StoreAnalyticsGrain,
  StoreAnalyticsReport,
  StoreAnalyticsSection,
} from '@ampersandbrands/ui';
import { apiGet } from './api';
import { RETAIL_API_BASE } from './useRetailStores';

export const STORE_ANALYTICS_API_BASE = `${RETAIL_API_BASE}/store-analytics`;

export const EMPTY_STORE_ANALYTICS_FILTERS: StoreAnalyticsFilters = {
  section: 'returns',
  grain: 'week',
  from: '',
  to: '',
  through: '',
  compare: 'last_year',
  store_no: [],
  disc: [],
};

const SECTIONS: readonly StoreAnalyticsSection[] = ['returns', 'discounts', 'bulk'];
const ISO_DAY = /^\d{4}-\d{2}-\d{2}$/;

function asSection(v: string | null): StoreAnalyticsSection {
  return SECTIONS.includes(v as StoreAnalyticsSection) ? (v as StoreAnalyticsSection) : 'returns';
}
function asGrain(v: string | null): StoreAnalyticsGrain {
  return v === 'day' ? 'day' : 'week';
}
function asCompare(v: string | null): StoreAnalyticsCompare {
  return v === 'none' ? 'none' : 'last_year';
}
function asDay(v: string | null): string {
  return v !== null && ISO_DAY.test(v) ? v : '';
}

/**
 * Query string for the report. Lists repeat their key; defaults are left out. Dates
 * only travel with the section they belong to (the window with Returns and Discounts,
 * the as-of day with Bulk), so switching tabs never sends the other tab's dates.
 */
export function buildStoreAnalyticsQs(f: StoreAnalyticsFilters): string {
  const sp = new URLSearchParams();
  if (f.section !== 'returns') sp.set('section', f.section);
  if (f.grain !== 'week') sp.set('grain', f.grain);
  if (f.section === 'bulk') {
    if (f.through) sp.set('through', f.through);
  } else {
    if (f.from) sp.set('from', f.from);
    if (f.to) sp.set('to', f.to);
  }
  if (f.compare !== 'last_year') sp.set('compare', f.compare);
  for (const s of f.store_no) sp.append('store_no', s);
  if (f.section === 'discounts') for (const d of f.disc) sp.append('disc', d);
  return sp.toString();
}

/** The same keys for the export, which always builds every section, so every date goes with it. */
export function buildStoreAnalyticsExportQs(f: StoreAnalyticsFilters): string {
  const sp = new URLSearchParams();
  if (f.grain !== 'week') sp.set('grain', f.grain);
  if (f.from) sp.set('from', f.from);
  if (f.to) sp.set('to', f.to);
  if (f.through) sp.set('through', f.through);
  if (f.compare !== 'last_year') sp.set('compare', f.compare);
  for (const s of f.store_no) sp.append('store_no', s);
  for (const d of f.disc) sp.append('disc', d);
  return sp.toString();
}

/** Read the filters back out of the page's URL. A date that is not a date is ignored (the default). */
export function parseStoreAnalyticsFilters(sp: URLSearchParams): StoreAnalyticsFilters {
  return {
    section: asSection(sp.get('section')),
    grain: asGrain(sp.get('grain')),
    from: asDay(sp.get('from')),
    to: asDay(sp.get('to')),
    through: asDay(sp.get('through')),
    compare: asCompare(sp.get('compare')),
    store_no: sp.getAll('store_no').filter(Boolean),
    disc: sp.getAll('disc').filter((d) => /^\d+$/.test(d)),
  };
}

/** Write the filters into the page's URL, leaving defaults out. Every key is kept so a tab switch keeps its dates. */
export function writeStoreAnalyticsFilters(sp: URLSearchParams, f: StoreAnalyticsFilters): void {
  for (const k of ['section', 'grain', 'from', 'to', 'through', 'compare', 'store_no', 'disc']) sp.delete(k);
  // Districts were removed from every report; drop a stale one from an old bookmarked URL.
  sp.delete('district');
  if (f.section !== 'returns') sp.set('section', f.section);
  if (f.grain !== 'week') sp.set('grain', f.grain);
  if (f.from) sp.set('from', f.from);
  if (f.to) sp.set('to', f.to);
  if (f.through) sp.set('through', f.through);
  if (f.compare !== 'last_year') sp.set('compare', f.compare);
  for (const s of f.store_no) sp.append('store_no', s);
  for (const d of f.disc) sp.append('disc', d);
}

/**
 * What a subscription saves. The store list is joined into a comma string because the
 * api keeps scalar filter values only. No date is ever saved: the email always covers
 * the default windows as of the morning it is sent. Defaults are left out, since an
 * unset filter is not a filter.
 */
export function storeAnalyticsSubscriptionFilters(f: StoreAnalyticsFilters): Record<string, unknown> {
  const out: Record<string, unknown> = {};
  if (f.store_no.length > 0) out.store_no = f.store_no.join(',');
  if (f.grain !== 'week') out.grain = f.grain;
  if (f.compare !== 'last_year') out.compare = f.compare;
  return out;
}

function isObject(v: unknown): boolean {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Tolerant read, so a payload that is absent or not the report shape degrades to "no
 * data yet" rather than a crash. Returns null unless the response carries at least one
 * of the three sections; the rest default to null.
 */
export function normalizeStoreAnalytics(raw: unknown): StoreAnalyticsReport | null {
  const r = (raw ?? {}) as Record<string, unknown>;
  const returns = isObject(r.returns) ? (r.returns as StoreAnalyticsReport['returns']) : null;
  const discounts = isObject(r.discounts) ? (r.discounts as StoreAnalyticsReport['discounts']) : null;
  const bulk = isObject(r.bulk) ? (r.bulk as StoreAnalyticsReport['bulk']) : null;
  if (!returns && !discounts && !bulk) return null;
  const section: StoreAnalyticsReport['section'] =
    r.section === 'all' || SECTIONS.includes(r.section as StoreAnalyticsSection)
      ? (r.section as StoreAnalyticsReport['section'])
      : returns
        ? 'returns'
        : discounts
          ? 'discounts'
          : 'bulk';
  return {
    section,
    as_of: typeof r.as_of === 'string' ? r.as_of : null,
    data_through: typeof r.data_through === 'string' ? r.data_through : null,
    data_from: typeof r.data_from === 'string' ? r.data_from : null,
    scope: r.scope === 'store' ? 'store' : 'fleet',
    compare: r.compare === 'none' ? 'none' : 'last_year',
    returns,
    discounts,
    bulk,
  };
}

export function useStoreAnalytics(filters: StoreAnalyticsFilters, enabled: boolean) {
  // Only what the api reads decides the cache key, so a date typed on another tab does not refetch this one.
  const qs = buildStoreAnalyticsQs(filters);
  const query = useQuery<StoreAnalyticsReport | null, Error>({
    queryKey: ['retail', 'store-analytics', qs],
    queryFn: async () =>
      normalizeStoreAnalytics(await apiGet<unknown>(qs ? `${STORE_ANALYTICS_API_BASE}?${qs}` : STORE_ANALYTICS_API_BASE)),
    // The mirrors refresh hourly; a few minutes of cache is plenty.
    staleTime: 5 * 60_000,
    retry: false,
    enabled,
    // Keep the old report on screen while a filter change loads, so the toolbar does not flicker.
    placeholderData: (prev) => prev,
  });
  return {
    report: query.data ?? null,
    isLoading: query.isLoading,
    error: query.error,
  };
}

/** Browser download of the Excel export (every section), following the filters on screen. */
export async function downloadStoreAnalyticsXlsx(filters: StoreAnalyticsFilters): Promise<void> {
  const qs = buildStoreAnalyticsExportQs(filters);
  const res = await fetch(`${STORE_ANALYTICS_API_BASE}/export${qs ? `?${qs}` : ''}`, { credentials: 'include' });
  if (!res.ok) {
    const text = await res.text().catch(() => '');
    throw new Error(`Export failed: ${res.status} ${text}`);
  }
  const blob = await res.blob();
  const m = (res.headers.get('content-disposition') ?? '').match(/filename="([^"]+)"/);
  const objectUrl = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = objectUrl;
  a.download = m?.[1] ?? 'store-analytics.xlsx';
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(objectUrl);
}
